import { Button, FlexBox } from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { change, Field } from 'redux-form';

import {
  messengerSettingsFormFields,
  ThemeColor,
} from 'components/settings/components/sections/dixaMessenger/views/appearance/types';

import { messengerAppearanceFormName } from '../../../messengerSettingsFormValueSelector';
import { FormSectionTitle } from '../../formSectionTitle';
import { ColorInputField } from '../components/ColorInputField';

import { ColorOptions } from './components/colorOptions/ColorOptions';

interface MessengerThemeProps {
  presetBackgrounds: ThemeColor[];
  backgroundColor: string;
}

type Mode = 'preset' | 'custom';

const bgColorFieldName = messengerSettingsFormFields.backgroundColor;

export const MessengerTheme = ({
  presetBackgrounds,
  backgroundColor,
}: MessengerThemeProps): JSX.Element => {
  const dispatch = useDispatch();
  function isPresetColor(color: string) {
    return presetBackgrounds.map(({ id }) => id).includes(color);
  }

  function resolveColor(themeIdOrCustomColor: string) {
    if (isPresetColor(themeIdOrCustomColor)) {
      return (
        presetBackgrounds.find(({ id }) => id === themeIdOrCustomColor)
          ?.background ?? themeIdOrCustomColor
      );
    }
    return themeIdOrCustomColor;
  }

  const initialMode: Mode = isPresetColor(backgroundColor)
    ? 'preset'
    : 'custom';

  const presetColorCodes = presetBackgrounds
    .map(({ background }) => background)
    .filter(Boolean) as string[];

  const [mode, setMode] = useState<Mode>(initialMode);

  function setBgColorFieldValue(value: string) {
    dispatch(change(messengerAppearanceFormName, bgColorFieldName, value));
  }

  function toggleMode() {
    switch (mode) {
      case 'preset':
        setMode('custom');
        // switching to custom mode, so convert color id to actual color
        setBgColorFieldValue(resolveColor(backgroundColor));
        break;

      case 'custom':
        setMode('preset');
        // switching to preset mode, so set it to default preset
        setBgColorFieldValue('midnight');
        break;
    }
  }

  return (
    <FlexBox
      gap="300"
      width="500px"
      flexDirection="column"
      alignItems="flex-start"
    >
      <FormSectionTitle
        title="Background color"
        description="Select a theme color"
      />

      <Field
        required
        name={bgColorFieldName}
        component={ColorOptions}
        options={presetBackgrounds}
      />

      {mode === 'custom' && (
        <Field
          required
          noLabel
          clearable={false}
          name={bgColorFieldName}
          component={ColorInputField}
          presetColors={presetColorCodes}
        />
      )}

      {mode === 'preset' && (
        <Button
          variant="tertiary"
          onClick={toggleMode}
          style={{ marginLeft: -12 }}
        >
          Or create a custom color
        </Button>
      )}
    </FlexBox>
  );
};
